/**
 * AP-773 — R20: the brief message shown when an undo / redo press finds
 * nothing to do.
 *
 * "Nothing to undo" / "Nothing to redo" on an empty stack; a stale step that
 * had to be dropped (R14) says so. A press that applies a step shows nothing
 * (decision 2026-09-18). Ant Design's message renders it fixed near the top
 * centre; one shared key means a newer message replaces the one still fading
 * instead of stacking beside it (AC30).
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import { message } from 'antd';
import type { HistoryPressResult } from './useHistoryHotkeys';

export const HISTORY_MESSAGE_KEY = 'ap773-history';
/** §5: on screen for 1.2 s. */
export const HISTORY_MESSAGE_SECONDS = 1.2;
/** §5: about 30 % down the viewport. */
const TOP_FRACTION = 0.3;

/** The message for a press, or null when the press applied a step. */
export function historyMessageText(result: HistoryPressResult): string | null {
  const verb = result.direction === 'undo' ? 'Undo' : 'Redo';
  if (result.kind === 'queued') return null;
  if (result.kind === 'empty') return `Nothing to ${result.direction}`;
  if (result.kind === 'discarded') {
    return `${verb} skipped: ${result.step.label} no longer exists`;
  }
  return null;
}

export function useHistoryFeedback(): {
  showResult: (result: HistoryPressResult) => void;
  contextHolder: ReactNode;
} {
  const [api, contextHolder] = message.useMessage({
    top: Math.round(window.innerHeight * TOP_FRACTION),
    maxCount: 1,
  });
  const showResult = useCallback(
    (result: HistoryPressResult) => {
      const content = historyMessageText(result);
      if (content === null) return;
      api.open({
        key: HISTORY_MESSAGE_KEY,
        type: 'info',
        content,
        duration: HISTORY_MESSAGE_SECONDS,
      });
    },
    [api]
  );
  return useMemo(
    () => ({ showResult, contextHolder }),
    [showResult, contextHolder]
  );
}
